import { Component, ElementRef, ViewChild, AfterViewInit, Input, Output, EventEmitter } from '@angular/core';
import ForceGraph3D from '3d-force-graph';
import { forceManyBody, forceLink } from 'd3-force-3d';
import * as THREE from 'three';
import SpriteText from 'three-spritetext';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass';

@Component({
  selector: 'app-domains-view',
  templateUrl: './domains-view.component.html',
  styleUrls: ['./domains-view.component.scss'],
})
export class DomainsViewComponent implements AfterViewInit {
  @ViewChild('graphContainer', { static: true }) graphEl!: ElementRef<HTMLDivElement>;
  @Output() statsChanged = new EventEmitter<{
    totalProjects: number;
    domains: number;
    contributors: number;
  }>();

  graph: any;
  rawData: { nodes: any[]; links: any[] } = { nodes: [], links: [] };

  currentLevel: number = 1;
  activeDomain: string | null = null;
  activeMainConcept: string | null = null;
  @Input() fullGraphData: { nodes: any[]; links: any[] } = { nodes: [], links: [] };

  selectedProject: any = null;

  ngAfterViewInit(): void {
    fetch('assets/domains_view_data.json')
      .then((res) => res.json())
      .then((data) => {
        this.rawData = data;
        this.initGraph();
      });

    setTimeout(() => {
      const scene = this.graph.scene?.();
      if (scene) {
        this.addStarfieldBackground(scene);
      } else {
        console.warn('Scene still undefined');
      }
    }, 500); // Adjust delay if needed
  }

  initGraph() {
    this.graph = new ForceGraph3D(this.graphEl.nativeElement)
      .width(this.graphEl.nativeElement.offsetWidth)
      .height(this.graphEl.nativeElement.offsetHeight)
      .graphData(this.filterLevel1Data())
      .nodeAutoColorBy('type')
      .nodeLabel((node: any) => node.id)
      .backgroundColor('#000003')
      .d3Force('charge', forceManyBody().strength(-400))
      .d3Force('link', forceLink().distance(250))
      .nodeThreeObject((node) => this.createNodeObject(node))
      .onEngineTick(() => {
        const gData = this.graph.graphData();
        gData.nodes.forEach((n: any) => {
          n.z = 0;
          n.vz = 0;
        });
      })
      .onNodeClick(this.handleNodeClick.bind(this))
      .onEngineTick(() => {
        // Force projects to stay within their rings
        const gData = this.graph.graphData();
        const mainConceptNode =
          this.currentLevel === 3 ? gData.nodes.find((n: any) => n.id === this.activeMainConcept) : null;

        if (this.currentLevel === 3 && mainConceptNode) {
          const centerX = mainConceptNode.x;
          const centerY = mainConceptNode.y;

          gData.nodes.forEach((node: any) => {
            if (node.type === 'project') {
              const maturity = (node.techMaturity || 'none').toLowerCase();
              const techMaturityRings: Record<string, number> = {
                adopt: 50,
                trial: 90,
                assess: 130,
                identified: 170,
                none: 210,
              };

              const radius = techMaturityRings[maturity] || techMaturityRings.none;
              const dx = node.x - centerX;
              const dy = node.y - centerY;
              const angle = Math.atan2(dy, dx);

              node.x = centerX + radius * Math.cos(angle);
              node.y = centerY + radius * Math.sin(angle);
              node.z = 0;
            }
          });
        }
      });

    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      1.5, // strength
      1.0, // radius
      0.0, // threshold
    );
    this.graph.postProcessingComposer().addPass(bloomPass);
  }

  private emitStats(nodes: any[]) {
    const projects = nodes.filter((n) => n.type === 'project');
    const contributorsSet = new Set<string>();
    const domainsSet = new Set<string>();

    projects.forEach((p) => {
      if (p.STIC_team)
        (Array.isArray(p.STIC_team) ? p.STIC_team : [p.STIC_team]).forEach((c: string) => contributorsSet.add(c));
      if (p.domains) (Array.isArray(p.domains) ? p.domains : [p.domains]).forEach((d: string) => domainsSet.add(d));
    });

    this.statsChanged.emit({
      totalProjects: projects.length,
      domains: domainsSet.size,
      contributors: contributorsSet.size,
    });
  }

  filterLevel1Data() {
    this.currentLevel = 1;
    this.activeDomain = null;
    this.activeMainConcept = null;
    this.selectedProject = null;

    const nodes = this.rawData.nodes.filter((n) => n.type === 'root' || n.type === 'domain');
    const links = this.rawData.links.filter((l) => {
      const sourceId = typeof l.source === 'string' ? l.source : l.source.id;
      return sourceId === 'Domains';
    });

    // Position domains dynamically based on mainConcept count
    const domains = nodes.filter((n) => n.type === 'domain');
    const center = { x: 0, y: 0, z: 0 };
    const angleStep = (2 * Math.PI) / domains.length;

    domains.forEach((d, i) => {
      const mainConceptCount = this.rawData.links.filter((l) => {
        const sourceId = typeof l.source === 'string' ? l.source : l.source.id;
        const targetNode = this.rawData.nodes.find((nn) => nn.id === l.target && nn.type === 'mainConcept');
        return sourceId === d.id && targetNode;
      }).length;

      d.mainConceptCount = mainConceptCount;
      d.distanceFromCenter = 150 + mainConceptCount * 20;

      const angle = i * angleStep;
      d.fx = center.x + d.distanceFromCenter * Math.cos(angle);
      d.fy = center.y + d.distanceFromCenter * Math.sin(angle);
      d.fz = 0;
    });

    const rootNode = nodes.find((n) => n.id === 'Domains');
    if (rootNode) {
      rootNode.fx = center.x;
      rootNode.fy = center.y;
      rootNode.fz = center.z;
    }

    const result = { nodes, links };
    this.emitStats(result.nodes);
    return result;
  }

  filterLevel2Data(domainId: string) {
    this.currentLevel = 2;
    this.activeDomain = domainId;
    this.activeMainConcept = null;
    this.selectedProject = null;

    const nodes = this.rawData.nodes.filter(
      (n) =>
        n.id === domainId ||
        (n.type === 'mainConcept' &&
          this.rawData.links.some((l) => {
            const sourceId = typeof l.source === 'string' ? l.source : l.source.id;
            const targetId = typeof l.target === 'string' ? l.target : l.target.id;
            return sourceId === domainId && targetId === n.id;
          })),
    );

    const links = this.rawData.links.filter((l) => {
      const sourceId = typeof l.source === 'string' ? l.source : l.source.id;
      return sourceId === domainId;
    });

    const domainNode = nodes.find((n) => n.id === domainId);
    const mainConcepts = nodes.filter((n) => n.type === 'mainConcept');

    const center = { x: 0, y: 0, z: 0 };
    if (domainNode) {
      domainNode.fx = center.x;
      domainNode.fy = center.y;
      domainNode.fz = center.z;
    }

    const angleStep = (2 * Math.PI) / mainConcepts.length;
    const ringSpacing = 1000;

    mainConcepts.forEach((mc, i) => {
      const angle = i * angleStep;
      mc.fx = center.x + ringSpacing * Math.cos(angle);
      mc.fy = center.y + ringSpacing * Math.sin(angle);
      mc.fz = 0;
    });

    const result = { nodes, links };
    this.emitStats(result.nodes);
    return result;
  }

  filterLevel3Data(mainConceptId: string) {
    this.currentLevel = 3;
    this.activeMainConcept = mainConceptId;
    this.selectedProject = null;

    const nodes = this.rawData.nodes.filter(
      (n) =>
        n.id === mainConceptId ||
        (n.type === 'project' &&
          this.rawData.links.some((l) => {
            const sourceId = typeof l.source === 'string' ? l.source : l.source.id;
            const targetId = typeof l.target === 'string' ? l.target : l.target.id;
            return sourceId === mainConceptId && targetId === n.id;
          })),
    );

    const links: any[] = [];
    const mainConceptNode = nodes.find((n) => n.id === mainConceptId);
    const projects = nodes.filter((n) => n.type === 'project');

    const center = { x: 0, y: 0, z: 0 };
    if (mainConceptNode) {
      mainConceptNode.fx = center.x;
      mainConceptNode.fy = center.y;
      mainConceptNode.fz = center.z;
    }

    const techMaturityRings: Record<string, number> = {
      adopt: 50,
      trial: 90,
      assess: 130,
      identified: 170,
      none: 210,
    };

    const groupedProjects: Record<string, any[]> = {
      adopt: [],
      trial: [],
      assess: [],
      identified: [],
      none: [],
    };

    projects.forEach((p) => {
      const maturity = (p.techMaturity || 'none').toLowerCase();
      (groupedProjects[maturity] || groupedProjects.none).push(p);
    });

    Object.keys(groupedProjects).forEach((maturity) => {
      const projectList = groupedProjects[maturity];
      const radius = techMaturityRings[maturity];
      const angleStep = (2 * Math.PI) / Math.max(projectList.length, 1);

      projectList.forEach((p, i) => {
        const angle = i * angleStep;
        p.fx = center.x + radius * Math.cos(angle);
        p.fy = center.y + radius * Math.sin(angle);
        p.fz = 0;
      });
    });

    const result = { nodes, links };
    this.emitStats(result.nodes);
    return result;
  }

  createNodeObject(node: any): THREE.Group {
    const group = new THREE.Group();

    let color: number | string = 0xeca83d;
    if (node.type === 'domain' || node.type === 'root') color = 0xeca83d;
    else if (node.type === 'mainConcept') color = 'white';

    if (node.type === 'root' || node.type === 'domain') {
      const radius = node.type === 'root' ? 30 : 20; // bigger radius for root
      const tubeRadius = 1.5;
      const radialSegments = 16;
      const tubularSegments = 100;

      // Base torus
      const geometry = new THREE.TorusGeometry(radius, tubeRadius, radialSegments, tubularSegments);
      const material = new THREE.MeshBasicMaterial({ color: color });
      const torus = new THREE.Mesh(geometry, material);
      group.add(torus);

      // Bloom torus (glow effect)
      const bloomGeometry = new THREE.TorusGeometry(radius, tubeRadius * 1.2, radialSegments, tubularSegments);
      const bloomMaterial = new THREE.MeshBasicMaterial({ color: 0xffd27f }); // warm glow
      const bloomTorus = new THREE.Mesh(bloomGeometry, bloomMaterial);
      bloomTorus.layers.set(1); // bloom layer
      group.add(bloomTorus);
    } else {
      // For mainConcept and project nodes → keep spheres
      const radius = node.type === 'project' ? 10 : 20;
      const sphere = new THREE.Mesh(new THREE.SphereGeometry(radius), new THREE.MeshBasicMaterial({ color }));
      group.add(sphere);
    }

    // Satellites for domain nodes
    if (node.type === 'domain' && node.mainConceptCount && this.currentLevel === 1) {
      const satelliteRadius = 35;
      for (let i = 0; i < node.mainConceptCount; i++) {
        const angle = (2 * Math.PI * i) / node.mainConceptCount;
        const x = satelliteRadius * Math.cos(angle);
        const y = satelliteRadius * Math.sin(angle);
        const geometry = new THREE.SphereGeometry(4, 16, 16);
        const material = new THREE.MeshBasicMaterial({ color: 0xeca83d });
        const satellite = new THREE.Mesh(geometry, material);
        satellite.position.set(x, y, 0);
        group.add(satellite);
      }
    }

    // Rings for mainConcept nodes
    if (node.type === 'mainConcept') {
      const labels = ['adopt', 'trial', 'assess', 'identified', 'none'];
      const ringMaterial = new THREE.MeshBasicMaterial({ color: 'gray', side: THREE.DoubleSide });
      labels.forEach((label, index) => {
        const ringRadius = 50 + index * 40;
        const ring = new THREE.Mesh(new THREE.RingGeometry(ringRadius - 1, ringRadius, 64), ringMaterial.clone());
        ring.rotation.z = Math.PI / 2;
        ring.position.y = -1.5 * index;
        group.add(ring);

        const ringLabel = new SpriteText(label);
        ringLabel.color = 'white';
        ringLabel.textHeight = 12;
        const angle = index * 0.5;
        const x = ringRadius * Math.cos(angle);
        const y = ringRadius * Math.sin(angle);
        ringLabel.position.set(x, y, 0);
        group.add(ringLabel);
      });
    }

    // Spiral for root node (background effect)
    if (node.type === 'root') {
      const texture = new THREE.TextureLoader().load('assets/spiral_domains.png');
      const material = new THREE.SpriteMaterial({ map: texture, transparent: true, opacity: 0.15 });
      const spiral = new THREE.Sprite(material);
      spiral.scale.set(165, 165, 1);
      spiral.position.set(0, 0, 0);
      group.add(spiral);
    }

    // Labels
    const label = new SpriteText(node.id);
    label.color = 'white';
    if (node.type === 'project') {
      label.textHeight = 10;
      label.position.set(0, 15, 0);
    } else if (node.type === 'root') {
      label.textHeight = 30;
      label.position.set(0, 45, 0);
    } else {
      label.textHeight = 20;
      label.position.set(0, 30, 0);
    }
    group.add(label);

    return group;
  }

  handleNodeClick(node: any) {
    if (this.currentLevel === 1 && node.type === 'domain') {
      this.graph.graphData(this.filterLevel2Data(node.id));
    } else if (this.currentLevel === 2 && node.type === 'mainConcept') {
      this.graph.graphData(this.filterLevel3Data(node.id));
    } else if (this.currentLevel === 3 && node.type === 'project') {
      // Show project popup info
      this.selectedProject = {
        id: node.id,
        domain: node.domains ? 'Domain: ' + node.domains : 'Domain: Unknown',
        techMaturity: node.techMaturity.length > 0 ? 'Tech Maturity: ' + node.techMaturity : 'Tech Maturity: Unknown',
        team: node.team.length > 0 ? 'Team: ' + node.team : 'Team: Unknown',
        STICTeam: node.STIC_team.length > 0 ? 'Team Members: ' + node.STIC_team : 'Team Members: Unknown',
        stakeholders: node.stakeholders.length > 0 ? 'Stakeholders: ' + node.stakeholders : 'Stakeholders: Unknown',
        mainConcept:
          node.mainConcepts.length > 0 ? 'Main Technologies: ' + node.mainConcepts : 'Main Technologies: Unknown',
        subConcept: node.subConcepts.length > 0 ? 'Sub Technologies: ' + node.subConcepts : 'Sub Technologies: Unknown',
        description: node.markdown ? 'Description: ' + node.markdown : 'No description available.',
        path: node.path || 'Unknown Path',
      };
    } else if (node.type === 'root') {
      // Reset to level 1 view
      this.graph.graphData(this.filterLevel1Data());
      this.selectedProject = null;
    }
  }

  closeInfoBox() {
    this.selectedProject = null;
  }

  resetGraph() {
    this.graph.graphData(this.filterLevel1Data());
    this.selectedProject = null;
  }

  centerGraph() {
    this.graph.zoomToFit(400);
  }

  addStarfieldBackground(scene: THREE.Scene) {
    const starsGeometry = new THREE.BufferGeometry();
    const starCount = 15000;

    const positions = new Float32Array(starCount * 3);
    for (let i = 0; i < starCount * 3; i++) {
      positions[i] = (Math.random() - 0.5) * 5000; // make this bigger if needed
    }

    starsGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const starsMaterial = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 2,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.7,
    });

    const stars = new THREE.Points(starsGeometry, starsMaterial);
    scene.add(stars);
  }
}
